import _ from 'lodash';
// @mui
import { Stack } from '@mui/system';
import { Box, Collapse, Typography } from '@mui/material';
//components
import FormCreator from 'components/FormCreator';
import { fieldType, invalidFields, fieldsGroupType } from 'components/FormCreator/types';
//reducers
import { selectLanguage, selectTranslations } from 'state/reducers/translation.reducers';
import { getNewPay, getPayFees, getPaymentDates } from 'state/reducers/pay.reducers';
import { useAppSelector } from 'hooks/redux.hooks';
//sections
import { FormCard } from 'theme/elements/Forms';
import { FormHeader1 } from 'theme/elements/Headers';
//constants
import { PayEnum, Pay, paymentsTypes, swiftTypes, PaymentFees } from 'constants/pay.constants';
import { useEffect, useMemo } from 'react';
import { LightGreenCard } from 'theme/elements/Cards';
import { format } from 'date-fns';
import { timing } from 'constants/ui.constants';

export default function PaymentPreferencesForm(props: {
  handleChange: any;
  invalidFields: invalidFields;
  currency: any;
  paymentFee: any;
}) {
  const t = useAppSelector(selectTranslations);
  const pay: Pay = useAppSelector(getNewPay);
  const prefixFields = 'page.pay.create.step.2.beneficiary_payment_preferences.';
  const selectedLang = useAppSelector(selectLanguage);
  const paymentDates: string[]= useAppSelector(getPaymentDates);

  const getDayOfWeek = (dateString: string, lang: 'en' | 'he'): string => {
    const date = new Date(dateString);
    const daysOfWeek = {
      en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      he: ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת']
    };
  
    return daysOfWeek[lang][date.getDay()];
  };

  const useDropdownOptions = (paymentDates: string[]) => {
    return paymentDates.map(date => {
      const [year, month, day] = date.split('-');
      const dayOfWeek = getDayOfWeek(date, selectedLang);
  
      return {
        label: `${dayOfWeek} - ${day}.${month}.${year}`,
        value: date
      };
    });
  };

  const dropdownOptions = useDropdownOptions(paymentDates);

  const getifDisabled = (type: paymentsTypes) => {
    return _.isEqual(type, paymentsTypes.LOCAL)
      ? !pay.beneficiary?.isLocalPaymentAllowed
      : !pay.beneficiary?.isPriorityPaymentAllowed;
  };

  return (
    <Box component="form" noValidate>
      <FormCard>
        <FormHeader1 title={t(`${prefixFields}title`)} text={t(`${prefixFields}text`)} />
        <Stack spacing={2}>
          <FormCreator
            initialValidation={props.invalidFields}
            name="localPayment"
            handleChange={props.handleChange}
            fieldsGroups={[
              {
                type: fieldsGroupType.col,
                fields: [
                  {
                    type: fieldType.select,
                    props: {
                      disabled: false,
                      required: true,
                      name: PayEnum.paymentDate,
                      label: t(`${prefixFields}date.text`),
                      value: pay[PayEnum.paymentDate],
                      options: dropdownOptions
                    },
                  },
                  {
                    type: fieldType.radio,
                    props: {
                      required: true,
                      name: PayEnum.paymentType,
                      label: t(`${prefixFields}payment_type.text`),
                      value: pay.paymentType,
                      options: [
                        {
                          value: paymentsTypes.LOCAL,
                          disabled: getifDisabled(paymentsTypes.LOCAL),
                          label: t(`${prefixFields}payment_type.${paymentsTypes.LOCAL}.text`),
                        },
                        {
                          value: paymentsTypes.PRIORITY,
                          disabled: getifDisabled(paymentsTypes.PRIORITY),
                          label: t(
                            `${prefixFields}payment_type.${paymentsTypes.PRIORITY}.text`
                          ),
                        },
                      ],
                    },
                  },
                ],
              },
            ]}
          />
          <Collapse in={_.isEqual(pay.paymentType, paymentsTypes.PRIORITY)}>
            <FormCreator
              initialValidation={props.invalidFields}
              name="localPayment"
              handleChange={props.handleChange}
              fieldsGroups={[
                {
                  type: fieldsGroupType.col,
                  fields: [
                    {
                      type: fieldType.radio,
                      props: {
                        required: true,
                        name: PayEnum.swift,
                        label: t(`${prefixFields}swift.text`),
                        value: pay.swift,
                        options: [
                          {
                            value: swiftTypes.SHARE,
                            label: t(`${prefixFields}swift.${swiftTypes.SHARE}.text`),
                          },
                          {
                            value: swiftTypes.OUR,
                            label: t(`${prefixFields}swift.${swiftTypes.OUR}.text`),
                          },
                        ],
                      },
                    },
                  ],
                },
              ]}
            />
          </Collapse>
          <LightGreenCard>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="boldBody1">{t(`${prefixFields}payment_fee.text`)}</Typography>
              <Typography>{_.toString(props.paymentFee)}</Typography>
            </Stack>
          </LightGreenCard>
        </Stack>
      </FormCard>
    </Box>
  );
}
